import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ClockIcon, ArrowRightIcon } from "@heroicons/react/24/outline";
import axios from "axios";

function LinkRedirect() {
  const { shortCode } = useParams();
  const navigate = useNavigate();
  const [linkData, setLinkData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [countdown, setCountdown] = useState(5);
  const [canSkip, setCanSkip] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchLinkData();
  }, [shortCode]);

  useEffect(() => {
    if (linkData && countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1);

        // Allow skip in last 2 seconds
        if (countdown <= 1) {
          setCanSkip(true);
        }
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [countdown, linkData]);

  const fetchLinkData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/redirect/${shortCode}`
      );

      if (response.data.success) {
        setLinkData(response.data.data);
        setCountdown(response.data.data.redirectDelay || 5);

        // Set page meta data
        document.title = `${response.data.data.link.title || "Redirecting..."} - KsLnk`;
      }
    } catch (error) {
      if (error.response?.status === 404) {
        setError("Link bulunamadı veya silinmiş");
      } else if (error.response?.status === 410) {
        setError("Link devre dışı bırakılmış veya süresi dolmuş");
      } else {
        setError("Link yüklenirken bir hata oluştu");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRedirect = () => {
    if (linkData?.link?.originalUrl) {
      window.location.href = linkData.link.originalUrl;
    }
  };

  const handleAdClick = (adUrl) => {
    if (adUrl && linkData?.advertisement?.id) {
      // Track ad click
      axios
        .post(`/api/admin/ads/${linkData.advertisement.id}/click`)
        .catch(() => {});
      window.open(adUrl, "_blank");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-600 via-primary-700 to-purple-800 flex items-center justify-center">
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full mx-4">
          <div className="text-center">
            <div className="w-16 h-16 bg-gradient-to-r from-primary-600 to-primary-700 rounded-xl flex items-center justify-center mx-auto mb-6 animate-pulse">
              <span className="text-white font-bold text-2xl">K</span>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              Link kontrol ediliyor...
            </h2>
            <div className="loading-spinner w-8 h-8 mx-auto"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-600 via-red-700 to-pink-800 flex items-center justify-center">
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full mx-4">
          <div className="text-center">
            <div className="w-16 h-16 bg-red-100 rounded-xl flex items-center justify-center mx-auto mb-6">
              <span className="text-red-600 font-bold text-2xl">✕</span>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              Bağlantı Hatası
            </h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <button onClick={() => navigate("/")} className="btn btn-primary">
              Ana Sayfaya Dön
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!linkData) {
    return null;
  }

  const progressPercentage =
    ((linkData.redirectDelay - countdown) / linkData.redirectDelay) * 100;

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-600 via-primary-700 to-purple-800 flex flex-col items-center justify-center p-4">
      {/* 728x90 ads */}
      {linkData.advertisement &&
        linkData.advertisement.ad_type === "728x90" && (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-1 mb-6 w-[728px] relative">
            <a
              href={linkData?.advertisement?.target_url}
              target="_blank"
              rel="noopener noreferrer"
            >
              {linkData.advertisement.image_url && (
                <img
                  src={linkData.advertisement.image_url}
                  alt={linkData.advertisement.title}
                />
              )}
            </a>
          </div>
        )}

      <div className="bg-white rounded-2xl shadow-2xl overflow-hidden max-w-lg w-full">
        {/* Header */}
        <div className="bg-gradient-to-r from-primary-600 to-primary-700 text-white p-6 text-center">
          <div className="w-12 h-12 bg-white bg-opacity-20 rounded-lg flex items-center justify-center mx-auto mb-4">
            <span className="text-2xl">🔗</span>
          </div>
          <h1 className="text-xl font-bold">KsLnk</h1>
          <p className="text-blue-100">Güvenli yönlendirme</p>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Countdown */}
          <div className="text-center mb-6">
            <div className="relative">
              <div className="w-24 h-24 mx-auto mb-4">
                <svg
                  className="w-24 h-24 transform -rotate-90"
                  viewBox="0 0 36 36"
                >
                  <path
                    className="text-gray-200"
                    stroke="currentColor"
                    strokeWidth="3"
                    fill="none"
                    d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                  />
                  <path
                    className="text-primary-600"
                    stroke="currentColor"
                    strokeWidth="3"
                    fill="none"
                    strokeDasharray={`${progressPercentage}, 100`}
                    d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-3xl font-bold text-primary-600">
                    {countdown}
                  </span>
                </div>
              </div>
            </div>

            <p className="text-gray-600 mb-4">
              {countdown > 0
                ? `${countdown} saniye sonra yönlendirileceksiniz`
                : "Yönlendiriliyor..."}
            </p>

            <button
              onClick={handleRedirect}
              disabled={!canSkip}
              className={`btn ${canSkip ? "btn-success" : "btn-secondary"} transition-all duration-300`}
            >
              {canSkip ? (
                <>
                  <ArrowRightIcon className="w-4 h-4 mr-2" />
                  Hemen Git
                </>
              ) : (
                <>
                  <ClockIcon className="w-4 h-4 mr-2" />
                  Lütfen Bekleyin
                </>
              )}
            </button>
          </div>

          {/* Advertisement */}
          {linkData.advertisement &&
            (linkData.advertisement.ad_type === "336x280" ||
              linkData.advertisement.ad_type === "300x250" ||
              linkData.advertisement.ad_type === "320x50" ||
              linkData.advertisement.ad_type === "300x100" ||
              linkData.advertisement.ad_type === "250x250") && (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
                <div className="text-center">
                  <h3 className="text-sm font-medium text-gray-700 mb-3">
                    Reklam
                  </h3>

                  {linkData.advertisement.image_url && (
                    <img
                      src={linkData.advertisement.image_url}
                      alt={linkData.advertisement.title}
                      className="w-full h-32 object-cover rounded-lg mb-3"
                    />
                  )}

                  <h4 className="font-semibold text-gray-900 mb-2">
                    {linkData.advertisement.title}
                  </h4>

                  {linkData.advertisement.description && (
                    <p className="text-sm text-gray-600 mb-3">
                      {linkData.advertisement.description}
                    </p>
                  )}

                  {linkData.advertisement.target_url && (
                    <button
                      onClick={() =>
                        handleAdClick(linkData.advertisement.target_url)
                      }
                      className="btn btn-primary btn-sm"
                    >
                      Siteyi Ziyaret Et
                    </button>
                  )}
                </div>
              </div>
            )}

          {/* Footer */}
          <div className="text-center text-sm text-gray-500">
            <p className="mb-2">Bu sayfa güvenliğiniz için gösterilmektedir</p>
            <p>
              Powered by{" "}
              <button
                onClick={() => navigate("/")}
                className="text-primary-600 hover:text-primary-700 font-medium"
              >
                KsLnk
              </button>
            </p>
          </div>
        </div>
      </div>

      {/* 970x250 ads */}
      {linkData.advertisement &&
        linkData.advertisement.ad_type === "970x250" && (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-1 mb-6 w-[728px] relative">
            <a
              href={linkData?.advertisement?.target_url}
              target="_blank"
              rel="noopener noreferrer"
            >
              {linkData.advertisement.image_url && (
                <img
                  src={linkData.advertisement.image_url}
                  alt={linkData.advertisement.title}
                />
              )}
            </a>
          </div>
        )}
    </div>
  );
}

export default LinkRedirect;
